import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { useOrganization } from '../hooks/useOrganization';
import { formatDateShort } from '../utils/dateFormat';

const AccountsPayable = () => {
  const { organization } = useOrganization();
  const [payables, setPayables] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [editingPayable, setEditingPayable] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchPayables = useCallback(async () => {
    try {
      const response = await axios.get(`/api/accounts-payable?status=${statusFilter}`);
      setPayables(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch accounts payable');
    }
  }, [statusFilter]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors');
      setVendors(response.data.vendors || []);
    } catch (error) {
      toast.error('Failed to fetch vendors');
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const response = await axios.get('/api/accounts-payable/purchase-orders');
      setPurchaseOrders(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch purchase orders');
    }
  };

  useEffect(() => {
    fetchPayables();
    fetchVendors();
    fetchPurchaseOrders();
  }, [fetchPayables]);

  const handleCreatePayable = async (formData) => {
    try {
      await axios.post('/api/accounts-payable', formData);
      toast.success('Accounts payable created successfully');
      setShowForm(false);
      fetchPayables();
      fetchPurchaseOrders(); // Refresh PO list to remove used PO
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create payable');
    }
  };

  const handleUpdatePayable = async (formData) => {
    try {
      await axios.put(`/api/accounts-payable/${editingPayable.id}`, formData);
      toast.success('Accounts payable updated successfully');
      setShowForm(false);
      setEditingPayable(null);
      fetchPayables();
      fetchPurchaseOrders(); // Refresh PO list
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update payable');
    }
  };

  const handlePayment = async (formData) => {
    try {
      await axios.post(`/api/accounts-payable/${selectedPayable.id}/pay`, formData);
      toast.success('Payment recorded successfully');
      setShowPaymentForm(false);
      setSelectedPayable(null);
      fetchPayables();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record payment');
    }
  };

  const handleEdit = (payable) => {
    setEditingPayable(payable);
    setShowForm(true);
  };

  const handleDelete = async (payableId) => {
    if (window.confirm('Are you sure you want to delete this payable?')) {
      try {
        await axios.delete(`/api/accounts-payable/${payableId}`);
        toast.success('Payable deleted successfully');
        fetchPayables();
        fetchPurchaseOrders(); // Refresh PO list to show deleted PO again
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete payable');
      }
    }
  };

  const PayableForm = ({ onSubmit, onCancel, editData = null }) => {
    const [formData, setFormData] = useState({
      vendorId: editData?.vendor_id || '',
      invoiceNumber: editData?.invoice_number || '',
      poNumber: editData?.po_number || '',
      invoiceDate: editData?.invoice_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      dueDate: editData?.due_date?.split('T')[0] || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      amount: editData?.amount || '',
      description: editData?.description || ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">{editData ? 'Edit' : 'Add'} Accounts Payable</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Vendor</label>
            <select
              value={formData.vendorId}
              onChange={(e) => setFormData({...formData, vendorId: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="">Select Vendor (Optional)</option>
              {vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Vendor Invoice/Ref#</label>
            <input
              type="text"
              value={formData.invoiceNumber}
              onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">PO Number</label>
            <select
              value={formData.poNumber}
              onChange={(e) => {
                const selectedPO = purchaseOrders.find(po => po.order_no === e.target.value);
                setFormData({
                  ...formData, 
                  poNumber: e.target.value,
                  vendorId: selectedPO?.vendor?.id || formData.vendorId,
                  amount: selectedPO?.total_amount || formData.amount
                });
              }}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="">Select Purchase Order (Optional)</option>
              {purchaseOrders.map(po => (
                <option key={po.id} value={po.order_no}>
                  {po.order_no} - {po.vendor?.name || 'No Vendor'} - {po.total_amount}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={formData.poNumber}
              onChange={(e) => setFormData({...formData, poNumber: e.target.value})}
              className="w-full px-3 py-2 border rounded-md mt-2"
              placeholder="Or enter PO number manually"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Invoice Date</label>
              <input
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({...formData, invoiceDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Due Date</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              rows="3"
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              {editData ? 'Update' : 'Create'} Payable
            </button>
            <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  const PaymentForm = ({ payable, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      paymentAmount: payable?.balance || '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'Cash',
      cashAccountId: '',
      notes: ''
    });
    const [cashAccounts, setCashAccounts] = useState([]);

    useEffect(() => {
      fetchCashAccounts();
    }, []);

    const fetchCashAccounts = async () => {
      try {
        const response = await axios.get('/api/accounts/cash');
        setCashAccounts(response.data || []);
      } catch (error) {
      }
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <p><strong>Invoice:</strong> {payable?.invoice_number}</p>
            <p><strong>Vendor:</strong> {payable?.vendor?.name || 'N/A'}</p>
            <p><strong>Outstanding:</strong> {formatCurrency(payable?.balance, organization?.currency_code)}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Payment Amount</label>
              <input
                type="number"
                step="0.01"
                max={payable?.balance}
                value={formData.paymentAmount}
                onChange={(e) => setFormData({...formData, paymentAmount: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Date</label>
              <input
                type="date"
                value={formData.paymentDate}
                onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Method *</label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Check">Check</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cash Account *</label>
              <select
                value={formData.cashAccountId}
                onChange={(e) => setFormData({...formData, cashAccountId: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="">Select Cash Account</option>
                {cashAccounts.map(account => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                rows="2"
              />
            </div>
            <div className="flex space-x-3">
              <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                Record Payment
              </button>
              <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-green-100 text-green-800';
      case 'PARTIAL': return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Accounts Payable</h2>
        <div className="flex space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="all">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="PARTIAL">Partial</option>
            <option value="PAID">Paid</option>
            <option value="OVERDUE">Overdue</option>
          </select>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Payable
          </button>
        </div>
      </div>

      {showForm ? (
        <PayableForm 
          onSubmit={editingPayable ? handleUpdatePayable : handleCreatePayable} 
          onCancel={() => { setShowForm(false); setEditingPayable(null); }}
          editData={editingPayable}
        />
      ) : (
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor Ref#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payables.map(payable => (
                  <tr key={payable.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{payable.invoice_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{payable.po_number || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{payable.vendor?.name || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(payable.amount, organization?.currency_code)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(payable.paid_amount, organization?.currency_code)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(payable.balance, organization?.currency_code)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDateShort(payable.due_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(payable.status)}`}>
                        {payable.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex space-x-2">
                        {payable.status !== 'PAID' && (
                          <button
                            onClick={() => { setSelectedPayable(payable); setShowPaymentForm(true); }}
                            className="text-green-600 hover:text-green-800"
                          >
                            Pay
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(payable)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(payable.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showPaymentForm && selectedPayable && (
        <PaymentForm
          payable={selectedPayable}
          onSubmit={handlePayment}
          onCancel={() => { setShowPaymentForm(false); setSelectedPayable(null); }}
        />
      )}
    </div>
  );
};

export default AccountsPayable;
